// eslint-disable-next-line @typescript-eslint/no-unused-vars
import {
    ArrayUnique,
    IsArray,
    IsBoolean,
    IsEnum,
    IsNotEmpty,
    IsOptional,
    IsString,
    ValidateIf,
} from 'class-validator';
import { format, parseISO } from 'date-fns';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import i18n from 'src/i18n';
import {
    EventIcon,
    EventRecurrenceFrequency,
    EventType,
    MunduEvent,
} from 'src/domain/event';

const __validatorDecorators = {
    ArrayUnique,
    IsArray,
    IsBoolean,
    IsEnum,
    IsNotEmpty,
    IsOptional,
    IsString,
    ValidateIf,
};
void __validatorDecorators;

export const NO_RECURRENCE = 'none';
export const NO_EVENT_ICON = '';

export type EventReadOnlyReason = 'ownership' | 'moduleMembership';

export class EventFormValues {
    @IsString({ message: () => i18n.t('validation.requiredField') })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    title = '';

    @IsOptional()
    @IsString()
    description = '';

    @IsOptional()
    @IsString()
    location = '';

    @IsString({ message: () => i18n.t('validation.requiredField') })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    startDate = '';

    @IsString({ message: () => i18n.t('validation.requiredField') })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    endDate = '';

    @IsOptional()
    @IsBoolean()
    allDay?: boolean;

    @IsOptional()
    @IsBoolean()
    requireResponse?: boolean;

    @IsEnum(EventType)
    type: EventType = EventType.residents;

    @ValidateIf((_form, value) => value !== NO_EVENT_ICON)
    @IsEnum(EventIcon)
    icon: EventIcon | typeof NO_EVENT_ICON = NO_EVENT_ICON;

    @IsString()
    recurrenceFrequency: EventRecurrenceFrequency | typeof NO_RECURRENCE =
        NO_RECURRENCE;

    @IsOptional()
    @IsString()
    recurrenceUntil = '';

    @IsOptional()
    @IsBoolean()
    sendToAll?: boolean;

    @IsOptional()
    @IsArray()
    @ArrayUnique()
    @IsString({ each: true })
    groupIds?: string[];
}

export const getBrowserTimeZone = () =>
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

export const responseDeadlineIsFuture = (
    startDate: string,
    allDay: boolean,
    timeZone: string,
    now: Date = new Date(),
): boolean => {
    if (!allDay) return new Date(startDate).getTime() > now.getTime();
    const [year, month, day] = startDate.slice(0, 10).split('-').map(Number);
    if (!year || !month || !day) return false;
    try {
        const formatter = new Intl.DateTimeFormat('en-GB', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
        const target = Date.UTC(year, month - 1, day, 8);
        let instant = target;
        for (let attempt = 0; attempt < 3; attempt++) {
            const parts = formatter.formatToParts(new Date(instant));
            const value = (name: string) =>
                Number(parts.find(part => part.type === name)?.value);
            const local = Date.UTC(
                value('year'),
                value('month') - 1,
                value('day'),
                value('hour'),
                value('minute'),
            );
            instant += target - local;
        }
        return instant > now.getTime();
    } catch {
        return false;
    }
};

export const getOriginalOccurrenceStart = (
    event: MunduEvent,
): string | undefined =>
    event.originalOccurrenceStartDate ??
    (event.occurrenceId?.startsWith(`${event.id}:`)
        ? event.occurrenceId.slice(event.id.length + 1)
        : undefined);

export const toEventFormInputValue = (isoDate: string) =>
    format(parseISO(isoDate), "yyyy-MM-dd'T'HH:mm");

export const replaceEventInputDate = (
    currentValue: string,
    nextDate: string,
    fallbackTime: string,
) => {
    if (!nextDate) return '';

    const currentTime = currentValue.includes('T')
        ? currentValue.slice(currentValue.indexOf('T') + 1)
        : fallbackTime;

    return `${nextDate}T${currentTime}`;
};

const ONE_HOUR_MS = 60 * 60 * 1000;

// moving the start to or past the end pushes the end along, keeping the
// duration the event had before the change
export const getEndDateAfterStartChange = (
    previousStart: string,
    nextStart: string,
    currentEnd: string,
): string | undefined => {
    const nextStartTime = new Date(nextStart).getTime();
    const endTime = new Date(currentEnd).getTime();
    if (Number.isNaN(nextStartTime) || Number.isNaN(endTime)) return undefined;
    if (endTime > nextStartTime) return undefined;

    const previousDuration = endTime - new Date(previousStart).getTime();
    const duration = previousDuration > 0 ? previousDuration : ONE_HOUR_MS;

    return format(new Date(nextStartTime + duration), "yyyy-MM-dd'T'HH:mm");
};

export const getRecurrenceLabelDate = (
    startDate: string,
    event?: MunduEvent,
): Date | undefined => {
    const nextOccurrenceStart = new Date(startDate);
    if (Number.isNaN(nextOccurrenceStart.getTime())) return undefined;

    if (!event?.recurrence || !event.seriesStartDate) {
        return nextOccurrenceStart;
    }

    const occurrenceStart = parseISO(event.startDate);
    const seriesStart = parseISO(event.seriesStartDate);

    return new Date(
        seriesStart.getTime() +
            (nextOccurrenceStart.getTime() - occurrenceStart.getTime()),
    );
};
